import React, { useEffect, useState } from 'react';
import {
    Button,
    Container,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Snackbar,
    Grid,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Alert } from '@material-ui/lab';
import SRCards from '../../components/SRCards';
import SelectCompany from '../../components/SelectCompany';
import { NotificationSettings, ReminderNotificationSetting } from './interface';
import LoadingModal from '../../components/LoadingModal';
import NotificationService from '../../services/notification.service';
import { CHECK_NON_EMPTY_DICTIONARY, CHECK_VALUE } from '../../common/data-validator';

function fillMissingWithDefaults(defaults: any, source: any): any {
    const result: any = {};
    for (const key of Object.keys(defaults)) {
        if (key in source && source[key] !== null && source[key] !== undefined) {
            if (
                typeof defaults[key] === 'object' &&
                !Array.isArray(defaults[key]) &&
                typeof source[key] === 'object'
            ) {
                result[key] = fillMissingWithDefaults(defaults[key], source[key]);
            } else {
                result[key] = source[key];
            }
        } else {
            result[key] = defaults[key];
        }
    }
    return result;
}


const defaultNotificationSettings: NotificationSettings = {
    email: {
        invoice: {
            SUCCESS: false,
            FAILED: false,
            DUE_DATE_REMINDER: { enabled: false, numberOfDays: 0 },
            OVERDUE_DATE_REMINDER: { enabled: false, numberOfDays: 0 },
            REFUNDED: false,
        },
        estimate: false,
        ticket: {
            CREATE: false,
            COMPLETE: false,
            CANCEL: false,
            SA_JOB_AUTO_CREATE: false,
            REMINDER: { enabled: false, numberOfDays: 0 },
        },
        assignment: {
            CREATE: false,
            COMPLETE: false,
            CANCEL: false,
            RESCHEDULE: false,
        },
        forms: false,
    },
    sms: {
        invoice: {
            SUCCESS: false,
            FAILED: false,
            DUE_DATE_REMINDER: { enabled: false, numberOfDays: 0 },
            OVERDUE_DATE_REMINDER: { enabled: false, numberOfDays: 0 },
            REFUNDED: false,
        },
        estimate: false,
        ticket: {
            CREATE: false,
            COMPLETE: false,
            CANCEL: false,
            SA_JOB_AUTO_CREATE: false,
            REMINDER: { enabled: false, numberOfDays: 0 },
        },
        assignment: {
            CREATE: false,
            COMPLETE: false,
            CANCEL: false,
            RESCHEDULE: false,
            DRIVING: false,
            TECH_REACHING: false,
        },
    },
};

export default function NotificationList() {
    const [notificationSettings, setNotificationSettings] =
        useState<NotificationSettings>(defaultNotificationSettings);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error',
    });
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [loadingModalState, setLoadingModalState] = useState(false);
    useEffect(() => {
        if(!CHECK_VALUE(selectedCompany?.value)) {
            return;
        }
        setLoadingModalState(true);
        NotificationService.getCompanyNotificationSetting()
        .then(res => {
            const rawData = CHECK_NON_EMPTY_DICTIONARY(res) ? res : {};
            const rawNormalized = { email: rawData.email ?? {}, sms: rawData.sms ?? {} };
            setNotificationSettings(fillMissingWithDefaults(defaultNotificationSettings, rawNormalized));
        })
        .catch(()=> {
            showSnackbar('Failed to get Notification permission Info', 'error');
        })
        .finally(()=>setLoadingModalState(false));
    }, [selectedCompany]);

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleToggle = <
        T extends 'email' | 'sms',
        S extends keyof NotificationSettings[T],
        K extends keyof NotificationSettings[T][S]
    >(
        notificationType: T,
        notificationSection: S,
        notificationName: K
    ) => {
        setNotificationSettings((prev) => {
            const updated = structuredClone(prev);
            const currentValue =
                updated[notificationType][notificationSection][
                    notificationName
                ];
            if (typeof currentValue === 'boolean') {
                updated[notificationType][notificationSection][
                    notificationName
                ] = !currentValue as any;
            }
            return updated;
        });
    };


    const handleSectionToggle = <
        T extends 'email' | 'sms',
        S extends keyof NotificationSettings[T]
    >(
        notificationType: T,
        notificationSection: S
    ) => {
        setNotificationSettings(prev => {
            const updated = structuredClone(prev);
            updated[notificationType][notificationSection] = !Boolean(updated[notificationType][notificationSection]) as any;
            return updated;
        });
    };

    const handleReminderToggle = <
        T extends 'email' | 'sms',
        S extends keyof NotificationSettings[T],
        K extends keyof NotificationSettings[T][S]
    >(
        notificationType: T,
        notificationSection: S,
        notificationName: K
    ) => {
        setNotificationSettings((prev) => {
            const updated = structuredClone(prev);
            const reminder = updated[notificationType][notificationSection][
                notificationName
            ] as ReminderNotificationSetting;
            reminder.enabled = !reminder.enabled;
            return updated;
        });
    };

    const handleDaysChange = <
        T extends 'email' | 'sms',
        S extends keyof NotificationSettings[T],
        K extends keyof NotificationSettings[T][S]
    >(
        notificationType: T,
        notificationSection: S,
        notificationName: K,
        value: number
    ) => {
        setNotificationSettings((prev) => {
            const updated = structuredClone(prev);
            const reminder = updated[notificationType][notificationSection][
                notificationName
            ] as ReminderNotificationSetting;
            reminder.numberOfDays = value;
            return updated;
        });
    };

    const saveSettings = () => {
        if (!CHECK_VALUE(selectedCompany.value)) {
            showSnackbar('Select a company first', 'error');
            return;
        }
        NotificationService.createOrUpdateNotification({...notificationSettings, companyId: selectedCompany.value});
        showSnackbar('Settings saved successfully!', 'success');
    };

    const renderReminder = <
        T extends 'email' | 'sms',
        S extends keyof NotificationSettings[T],
        K extends keyof NotificationSettings[T][S]
    >(
        notificationType: T,
        notificationSection: S,
        notificationName: K
    ) => {
        const reminderSection = notificationSettings[notificationType][
            notificationSection
        ][notificationName] as ReminderNotificationSetting;

        return (
            <Grid
                container
                key={String(notificationName)}
                alignItems='center'
                spacing={2}
                style={{ marginBottom: 8 }}
            >
                <Grid item>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={reminderSection.enabled}
                                onChange={() =>
                                    handleReminderToggle(
                                        notificationType,
                                        notificationSection,
                                        notificationName
                                    )
                                }
                                color='primary'
                            />
                        }
                        label={String(notificationName).replace(/_/g, ' ')}
                    />
                </Grid>
                {reminderSection.enabled && (
                    <Grid item>
                        <TextField
                            label='Days'
                            type='number'
                            value={reminderSection.numberOfDays}
                            onChange={(e) =>
                                handleDaysChange(
                                    notificationType,
                                    notificationSection,
                                    notificationName,
                                    parseInt(e.target.value, 10)
                                )
                            }
                            style={{ width: 100 }}
                            size='small'
                        />
                    </Grid>
                )}
            </Grid>
        );
    };

    const isReminderSetting = (
        obj: any
    ): obj is ReminderNotificationSetting => {
        return (
            obj &&
            typeof obj === 'object' &&
            'enabled' in obj &&
            'numberOfDays' in obj
        );
    };

    const renderSection = <
        T extends 'email' | 'sms',
        S extends keyof NotificationSettings[T]
    >(
        notificationType: T,
        notificationSection: S,
        notificationData: NotificationSettings[T][S]
    ) => {
        const typedKeys = Object.keys(notificationData as any) as Array<
            keyof NotificationSettings[T][S]
        >;

        return (
            <Accordion key={String(notificationSection)} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant='h6'>
                        {String(notificationSection).toUpperCase()}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: 'column' }}>
                    {typedKeys.map((notificationName) => {
                        const notificationValue =
                            notificationData[notificationName];

                        if (typeof notificationValue === 'boolean') {
                            return (
                                <FormControlLabel
                                    key={String(notificationName)}
                                    control={
                                        <Switch
                                            checked={notificationValue}
                                            onChange={() =>
                                                handleToggle(
                                                    notificationType,
                                                    notificationSection,
                                                    notificationName
                                                )
                                            }
                                            color='primary'
                                        />
                                    }
                                    label={String(notificationName).replace(
                                        /_/g,
                                        ' '
                                    )}
                                />
                            );
                        }

                        if (isReminderSetting(notificationValue)) {
                            return renderReminder(
                                notificationType,
                                notificationSection,
                                notificationName
                            );
                        }

                        return null;
                    })}
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <>
            <SRCards />
            <Container>
                <SelectCompany optionHandler={setSelectedCompany} />
                <Typography
                    variant='h5'
                    style={{ marginTop: 20, marginBottom: 10 }}
                >
                    Notification Settings
                </Typography>

                {(['email', 'sms'] as const).map((notificationType) => {
                    const notificationConfig =
                        notificationSettings[notificationType];
                    return (
                        <Accordion key={notificationType} defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant='h6'>
                                    {notificationType.toUpperCase()}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                style={{ flexDirection: 'column' }}
                            >
                                {(
                                    Object.keys(notificationConfig) as Array<
                                        keyof NotificationSettings[typeof notificationType]
                                    >
                                ).map((notificationSection) => {
                                    const notificationData =
                                        notificationConfig[notificationSection];
                                    if (typeof notificationData === 'boolean') {
                                        return (
                                            <FormControlLabel
                                                key={String(
                                                    notificationSection
                                                )}
                                                control={
                                                    <Switch
                                                        checked={
                                                            notificationData
                                                        }
                                                        onChange={() =>
                                                            handleSectionToggle(
                                                                notificationType,
                                                                notificationSection as any,
                                                            )
                                                        }
                                                        color='primary'
                                                    />
                                                }
                                                label={String(
                                                    notificationSection
                                                ).replace(/_/g, ' ')}
                                            />
                                        );
                                    }
                                    return renderSection(
                                        notificationType,
                                        notificationSection,
                                        notificationData
                                    );
                                })}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}

                <Button
                    variant='contained'
                    color='primary'
                    style={{ marginTop: 20 }}
                    onClick={saveSettings}
                >
                    Save Settings
                </Button>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                        }
                        severity={snackbar.severity}
                        variant='filled'
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
                <LoadingModal openState={loadingModalState} />

            </Container>
        </>
    );
}
